import { useEligibleProgramCount } from '@cibo/landmanager'
import {
  AuthUserPermission,
  BrandLogo,
  Can,
  ContactSupportButton,
  DebugProfile,
  useOrganizationContent,
  usePermissions,
  useUser,
} from '@cibo/profile'
import { RichTestFieldWidget } from '@cibo/programs'
import { AppVersion, CiboUiContext } from '@cibo/ui'
import BugReportRounded from '@mui/icons-material/BugReportRounded'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import GradingRounded from '@mui/icons-material/GradingRounded'
import LightModeRounded from '@mui/icons-material/LightModeRounded'
import LogoutRounded from '@mui/icons-material/LogoutRounded'
import PersonRounded from '@mui/icons-material/PersonRounded'
import SupportRounded from '@mui/icons-material/SupportRounded'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  GlobalStyles,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import { useAgreements } from 'features/Agreements'
import { FC, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { ADMIN_ROUTES, ROUTE } from '../../navigation/types'
import { GROWERS_ROUTES } from '../../navigation/types/growersRoutes'
import {
  ACCOUNT_PATHS,
  ACCOUNT_ROUTES,
  LAND_MANAGER_ROUTES,
  SCALED_ANALYSIS_ROUTES,
} from '../../navigation/types/routes'
import { SCOPE_THREE_ROUTES } from '../../navigation/types/scopeThreeRoutes'
import { useUnprofiledFieldsCount } from '../../pages/ProfileFields'
import { TranslationTester } from '../../pages/TranslationTester'
import { useHasSomeAdminAccess } from '../AdminLayout'
import { CreateBugReport } from '../CreateBugReport'
import { PageLink, SecondaryNavigation } from '../SecondaryNavigation'
import { ZendeskWidget } from '../ZendeskWidget'
import { Footer } from './Footer'

export const ImpactLayout: FC<{ hideFooter: boolean }> = ({ children, hideFooter }) => {
  const { t } = useTranslation('impact/components/ImpactLayout')
  const { can, permissions } = usePermissions()
  const hasSomeAdminAccess = useHasSomeAdminAccess()
  const user = useUser()
  const unprofiledFieldsCount = useUnprofiledFieldsCount()
  const organization = useOrganizationContent()
  const { pathname } = useLocation()

  const eligibleProgramCount = useEligibleProgramCount({})
  const { colorMode } = useContext(CiboUiContext)

  const { data: pendingAgreements } = useAgreements({
    userId: user.data?.user.userId,
    status: 'pending',
  })

  const pageLinks = useMemo(
    () =>
      [
        hasSomeAdminAccess ? { label: t('Status'), to: ADMIN_ROUTES.ROOT } : undefined,
        {
          label: t('Fields'),
          to: LAND_MANAGER_ROUTES.ROOT,
          count: unprofiledFieldsCount.data?.numAvailable,
          countColor: 'warning',
        },
        {
          label: t('Map'),
          to: LAND_MANAGER_ROUTES.MAP,
          exact: true,
        },
        {
          label: t('Programs'),
          to: ROUTE.PROGRAMS,
          count: eligibleProgramCount.data,
          countColor: 'info',
        },
        can({
          useAny: [
            AuthUserPermission.SUPPLY_SHED,
            AuthUserPermission.INSIGHTS,
            AuthUserPermission.CARBON_LAB,
            AuthUserPermission.INSIGHTS_LAB,
          ],
        })
          ? { label: t('Scaled Analysis'), to: SCALED_ANALYSIS_ROUTES.ROOT }
          : undefined,
        can({ useAny: AuthUserPermission.LEARN_VIEW })
          ? { label: t('Learn'), to: '/learn' }
          : undefined,
        can({ useAny: AuthUserPermission.GROWERS_MODULE })
          ? { label: t('Growers'), to: GROWERS_ROUTES.ROOT }
          : undefined,
        can({
          useAny: [
            AuthUserPermission.SCOPE3_INVENTORY_CREATE,
            AuthUserPermission.SCOPE3_INVENTORY_WRITE,
            AuthUserPermission.SCOPE3_INVENTORY_READ,
          ],
        })
          ? { label: t('Inventories'), to: SCOPE_THREE_ROUTES.ROOT }
          : undefined,
      ].filter(link => !!link) as PageLink[],

    [
      eligibleProgramCount.data,
      unprofiledFieldsCount.isFetching,
      hasSomeAdminAccess,
      permissions.join(), //indirect dependency of can()
    ]
  )

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement>()
  const [debugPanelOpen, setDebugPanelOpen] = useState(false)
  const closeMenu = () => setMenuAnchor(undefined)

  return (
    <>
      <Stack direction="row" sx={{ overflowX: 'hidden' }}>
        <GlobalStyles styles={{ ul: { margin: 0, padding: 0, listStyle: 'none' } }} />
        <CssBaseline />

        <Stack
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            width: `100%`,
            minHeight: '100vh',
          }}
        >
          <AppBar position="static">
            <Toolbar sx={{ justifyContent: 'space-between' }} variant="dense">
              <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                <BrandLogo size="small" variant="square" />
                <SecondaryNavigation pageLinks={pageLinks} sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Badge
                  color="warning"
                  badgeContent={
                    pendingAgreements && can({ useAny: AuthUserPermission.AGREEMENTS })
                      ? pendingAgreements.numAvailable
                      : 0
                  }
                  overlap="circular"
                >
                  <IconButton
                    size="small"
                    edge="end"
                    color="inherit"
                    aria-label="menu"
                    onClick={e => setMenuAnchor(e.currentTarget)}
                    sx={{ marginRight: 1 }}
                    data-testid="account-menu"
                  >
                    <Avatar sx={{ width: 32, height: 32, fontSize: '1rem' }}>
                      {user.data?.displayInitials}
                    </Avatar>
                  </IconButton>
                </Badge>
                <Menu
                  id="basic-menu"
                  anchorEl={menuAnchor}
                  open={!!menuAnchor}
                  onClose={() => setMenuAnchor(undefined)}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  <Box width={240}>
                    <Can useAny={AuthUserPermission.DEBUG_USER}>
                      <>
                        <MenuItem
                          onClick={() => {
                            setDebugPanelOpen(true)
                            closeMenu()
                          }}
                        >
                          <ListItemIcon>
                            <BugReportRounded fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText color="error">DEBUG Tools</ListItemText>
                        </MenuItem>
                        <Divider />
                      </>
                    </Can>
                    <MenuItem
                      component={Link}
                      to={ACCOUNT_ROUTES.PROFILE}
                      selected={
                        pathname.startsWith(ACCOUNT_ROUTES.ROOT) &&
                        !pathname.includes(ACCOUNT_PATHS.AGREEMENTS)
                      }
                      onClick={closeMenu}
                      data-testid="profile-menu-item"
                    >
                      <ListItemIcon>
                        <PersonRounded fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('profile')}</ListItemText>
                    </MenuItem>
                    <Can useAny={AuthUserPermission.AGREEMENTS}>
                      <MenuItem
                        component={Link}
                        to={ACCOUNT_ROUTES.AGREEMENTS}
                        selected={pathname.startsWith(ACCOUNT_ROUTES.AGREEMENTS)}
                        onClick={closeMenu}
                      >
                        <ListItemIcon>
                          <GradingRounded fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('agreements')}</ListItemText>
                        <Badge
                          color="warning"
                          badgeContent={pendingAgreements ? pendingAgreements.numAvailable : 0}
                        />
                      </MenuItem>
                    </Can>
                    <MenuItem
                      onClick={() => {
                        colorMode.toggleColorMode()
                      }}
                    >
                      <ListItemIcon>
                        {colorMode.currentMode === 'dark' ? (
                          <LightModeRounded />
                        ) : (
                          <DarkModeOutlined />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        {t('colorMode', { context: colorMode.currentMode })}
                      </ListItemText>
                    </MenuItem>
                    {organization.isLoading ? (
                      <MenuItem disabled>
                        <ListItemIcon>
                          <SupportRounded fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('help')}</ListItemText>
                      </MenuItem>
                    ) : (
                      <>
                        {organization.data?.fields.supportInformation.fields.enableZendesk ? (
                          <ZendeskWidget
                            position={{ horizontal: 'right', vertical: 'bottom' }}
                            onClick={closeMenu}
                          >
                            <MenuItem>
                              <ListItemIcon>
                                <SupportRounded fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('help')}</ListItemText>
                            </MenuItem>
                          </ZendeskWidget>
                        ) : (
                          <ContactSupportButton onClose={closeMenu}>
                            <MenuItem>
                              <ListItemIcon>
                                <SupportRounded fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('help')}</ListItemText>
                            </MenuItem>
                          </ContactSupportButton>
                        )}
                      </>
                    )}
                    <Divider />
                    <MenuItem component={Link} to={ROUTE.ACCOUNT_LOGOUT} onClick={closeMenu}>
                      <ListItemIcon>
                        <LogoutRounded fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('logout')}</ListItemText>
                    </MenuItem>
                    <MenuItem disabled>
                      <ListItemIcon />
                      <div style={{ pointerEvents: 'all' }}>
                        <AppVersion variant="caption" />
                      </div>
                    </MenuItem>
                  </Box>
                  {/* @todo - permission for JIRA */}
                  <Can useAny={AuthUserPermission.DEBUG_USER}>
                    <Divider />
                    <Stack flex={1} px={2} pt={1}>
                      <CreateBugReport color="error" variant="outlined" />
                    </Stack>
                  </Can>
                </Menu>
              </Stack>
            </Toolbar>
          </AppBar>
          <Stack flex={1}>{children}</Stack>
          {!hideFooter && <Footer />}
        </Stack>
      </Stack>
      <Can useAny={AuthUserPermission.CREATE_BUG_REPORT}>
        <Drawer open={debugPanelOpen} onClose={() => setDebugPanelOpen(false)} anchor="right">
          <Stack spacing={2} padding={2}>
            <Typography>Debug Panel</Typography>

            <a href="/m/static/bundles.html">Bundle Analysis</a>

            <RichTestFieldWidget />
            <DebugProfile />
            <TranslationTester />
          </Stack>
        </Drawer>
      </Can>
    </>
  )
}
